import React from "react";
import { Link, useLocation } from "react-router-dom";

import { getValidToken } from "../../utils/auth";
import { useActiveBusiness } from "../../hooks/useActiveBusiness";
import {
  ArrowRightIcon,
  CalendarCheckIcon,
  CardIcon,
  StorefrontIcon,
  TeamIcon,
} from "../public/icons";
import "./ProfessionalAccountRequired.css";

// Shown in place of a dashboard page when the signed-in account simply has no
// professional side to it (design 5a). This is not an error: the session is
// valid and the request worked, so the old role="alert" fallback -- and the
// silent bounce to "/" that ran a frame later -- both read as a bug and left
// the only real next step, signing up, undiscoverable.
//
// Customer-facing copy says "business account", never "professional": that is
// the role name in the codebase, and to a customer it sounds like a status they
// have to qualify for. For the same reason the role badge reads "Personal
// account" -- nobody thinks of themselves as a customer.

// /signup is wrapped in AuthPageGuard, which bounces a signed-in visitor to
// their role home. addAccount tells it this arrival is deliberate; source is
// plain attribution. Both survive the hop to /signup/details, which preserves
// the query string.
export const PRO_SIGNUP_PATH = "/signup?addAccount=1&source=pro-gate";

const BENEFITS = [
  { icon: StorefrontIcon, text: "A public booking page with your services and prices" },
  { icon: CalendarCheckIcon, text: "A calendar that holds your hours, buffers and reminders" },
  { icon: CardIcon, text: "Deposits, card payments and payouts to your account" },
  { icon: TeamIcon, text: "Team Mode if you work with others — add it when you sign up" },
];

// At most two glyphs, mirroring the dashboard avatar.
const getInitials = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts.slice(0, 2).map((part) => part[0].toUpperCase()).join("");
};

const ProfessionalAccountRequired = () => {
  const location = useLocation();
  const { payload } = useActiveBusiness();
  // payload is the /api/auth/me user once the workspace bootstrap has settled.
  // If that call failed it falls back to the JWT claims, which carry a name but
  // no email -- hence the identity row degrades to a single line rather than
  // rendering an empty one.
  const name = payload?.name || getValidToken()?.payload?.name || "";
  const email = payload?.email || "";
  const primaryIdentity = name || email || "Your account";
  const secondaryIdentity = name ? email : "";

  // Most people land here from a link or a bookmark, so "wrong account" is a
  // real case: send them back to the page they were trying to open.
  const nextPath = `${location.pathname}${location.search}${location.hash}`;
  const signInPath = `/login?next=${encodeURIComponent(nextPath)}`;

  return (
    <section className="pro-gate">
      <div className="pro-gate__card">
        <div className="pro-gate__intro">
          <span className="pro-gate__badge">
            <span className="pro-gate__badge-dot" aria-hidden="true" />
            Business account needed
          </span>
          <h1 className="pro-gate__title">This is the side of Haastia for taking bookings</h1>
          <p className="pro-gate__lede">
            Nothing went wrong — this dashboard is where people who take bookings run their
            day. Your account is set up for making them. Set up a business account and
            you&rsquo;ll get a dashboard of your own.
          </p>
        </div>

        <div className="pro-gate__identity-wrap">
          <div className="pro-gate__identity">
            <span className="pro-gate__avatar" aria-hidden="true">{getInitials(name)}</span>
            <div className="pro-gate__identity-text">
              <p className="pro-gate__identity-name">{primaryIdentity}</p>
              {secondaryIdentity && (
                <p className="pro-gate__identity-email">{secondaryIdentity}</p>
              )}
            </div>
            <span className="pro-gate__role">Personal account</span>
          </div>
        </div>

        <div className="pro-gate__benefits">
          <h2 className="pro-gate__benefits-title">What a business account adds</h2>
          <ul className="pro-gate__benefit-list">
            {BENEFITS.map(({ icon: Icon, text }) => (
              <li className="pro-gate__benefit" key={text}>
                <Icon size={18} strokeWidth={2.25} className="pro-gate__benefit-icon" />
                <p className="pro-gate__benefit-text">{text}</p>
              </li>
            ))}
          </ul>
        </div>

        <div className="pro-gate__reassurance">
          <p className="pro-gate__note">
            Your existing account stays exactly as it is — bookings, messages and history are
            untouched.
          </p>
        </div>

        <div className="pro-gate__actions">
          <Link className="pro-gate__btn pro-gate__btn--primary" to={PRO_SIGNUP_PATH}>
            Set up a business account
            <ArrowRightIcon size={16} className="pro-gate__btn-icon" />
          </Link>
          <Link className="pro-gate__btn" to="/">Back to browsing</Link>
          <span className="pro-gate__switch">
            Already taking bookings?{" "}
            <Link className="pro-gate__switch-link" to={signInPath}>
              Sign in with that account
            </Link>
          </span>
        </div>
      </div>
    </section>
  );
};

export default ProfessionalAccountRequired;
